import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { ButtonLoader, LoadingScreen, MultiCheckDropdown } from "Components";
import { newAuthService } from "Service/Axios";
import {
  getGlobalDataDetailsUrl,
  getGlobalDataListUrl,
  updateGlobalDataUrl,
} from "Constant/api";

export const MasterFieldDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gid: fieldGid } = useParams();

  const defaultFormData = { key: "", value: "", parentKey: [] };

  const [loading, setLoading] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);
  const [editIdx, setEditIdx] = useState(-1);
  const [fieldDetails, setFieldDetails] = useState(null);
  const [parentFieldList, setParentFieldList] = useState([]);
  const [parentKeyOptions, setParentKeyOptions] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);

  const { key, value, parentKey } = formData;

  // Function to handle change in form inputs
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleChangeParenytKey = (selectedOptions) => {
    setFormData((prev) => ({ ...prev, parentKey: selectedOptions }));
  };

  const handleEditField = (fieldData, fieldIdx) => {
    setEditIdx(fieldIdx);

    const selectedParentKeys = parentKeyOptions.filter((option) =>
      fieldData.parentkey?.includes(option.value)
    );
    setFormData({
      key: fieldData.key || "",
      value: fieldData.value || "",
      parentKey: selectedParentKeys,
    });
  };

  // Function to fetch field details
  const fetctParentFieldList = async () => {
    setLoading(true);
    const payload = {};
    const response = await newAuthService(
      getGlobalDataListUrl,
      "POST",
      payload
    );
    if (response.status) {
      const list = response.data.filter((item) => item.gid !== fieldGid);
      setParentFieldList(list);
      fetchFieldDetails(list);
    } else {
      setParentFieldList([]);
      setLoading(false);
    }
  };

  const fetchFieldDetails = async (pFieldList) => {
    const payload = { gid: fieldGid };
    const response = await newAuthService(
      getGlobalDataDetailsUrl,
      "POST",
      payload
    );
    if (response.status) {
      setFieldDetails(response.data);

      // set parent key options if parentFieldGid exists
      if (response.data.parentFieldGid) {
        const currentField = pFieldList.find(
          (item) => item.gid === response.data.parentFieldGid
        );
        setParentKeyOptions(
          currentField?.details?.map((item) => ({
            value: item.key,
            label: item.value,
          })) || []
        );
      } else {
        setParentKeyOptions([]);
      }
      setLoading(false);
    } else {
      setFieldDetails(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetctParentFieldList();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  // Function to save parent field change
  const onSaveParentKey = async () => {
    try {
      setSavedLoading(true);
      const newDetails = {
        key,
        value,
        parentkey: parentKey.map((item) => item.value),
      };

      const updatedFieldDetails = {
        ...fieldDetails,
        details: [...(fieldDetails?.details || [])],
      };

      // Check if editing (by matching key)
      if (editIdx !== -1) {
        updatedFieldDetails.details[editIdx] = newDetails;
      } else {
        updatedFieldDetails.details.push(newDetails);
      }

      const payload = {
        gid: fieldGid,
        details: updatedFieldDetails.details,
      };

      const response = await newAuthService(
        updateGlobalDataUrl,
        "POST",
        payload
      );
      if (response.status) {
        toast.success(t("common.messages.notification.success.updateField"));
        setFormData(defaultFormData);
        setEditIdx(-1);
        fetchFieldDetails(parentFieldList);
        setSavedLoading(false);
      } else {
        toast.error(response.message);
        setSavedLoading(false);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
      setSavedLoading(false);
    }
  };

  const onResetParentKey = () => {
    setFormData(defaultFormData);
  };

  const onRemoveKeyValue = async (key) => {
    try {
      const updatedFieldDetails = fieldDetails?.details?.filter(
        (item) => item.key !== key
      );
      const payload = {
        gid: fieldGid,
        details: updatedFieldDetails,
      };

      const response = await newAuthService(
        updateGlobalDataUrl,
        "POST",
        payload
      );
      if (response.status) {
        toast.success(t("common.messages.notification.success.updateField"));
        setFormData(defaultFormData);
        fetchFieldDetails(parentFieldList);
        setSavedLoading(false);
      } else {
        toast.error(response.message);
        setSavedLoading(false);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  return (
    <main className="main">
      <div className="">
        <div className="page-title-box d-flex justify-content-start align-items-center gap-3 w-100 mb-3 pb-1 border-bottom border-grey-200">
          <button
            onClick={handleBack}
            className="btn btn-back btn-tranparent d-flex justify-content-center align-items-center border-0 rounded-circle"
          >
            <i className="bx bx-arrow-back bx-sm"></i>
          </button>
          <h4 className="page-title">{fieldDetails?.name}</h4>

          <div className="ms-auto">
            <span>Parent Field: </span>
            <span className="text-base font-semibold text-primary-500">
              {fieldDetails?.parentFieldName || "No Parent Field"}
            </span>
          </div>
        </div>

        <div className="row gx-2">
          <div className="col-md-3">
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Enter Key"
                name="key"
                value={key}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Enter value"
                name="value"
                value={value}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex gap-2 mb-3">
              <div className="w-100 search-tab">
                <MultiCheckDropdown
                  options={parentKeyOptions}
                  value={parentKey}
                  onChange={handleChangeParenytKey}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                {!savedLoading ? (
                  <Button variant="primary" onClick={onSaveParentKey}>
                    {t("common.button.save")}
                  </Button>
                ) : (
                  <ButtonLoader className="btn-loading-sm" />
                )}
                <Button variant="light" onClick={onResetParentKey}>
                  Reset
                </Button>
              </div>              
            </div>
          </div>
        </div>

        <div className="table-container">
          <div className="table-container project-table-height">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Parent Key</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="loading-screen">
                    <td colSpan={5}>
                      <LoadingScreen />
                    </td>
                  </tr>
                ) : (
                  <>
                    {fieldDetails?.details?.length > 0 ? (
                      fieldDetails?.details.map((field, idx) => (
                        <tr key={`${field.gid}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{field.key}</td>
                          <td>{field.value}</td>
                          <td>
                            {field.parentkey && field.parentkey.length > 0
                              ? field.parentkey.join(", ")
                              : "-"}
                          </td>
                          <td>
                            <div className="d-flex justify-content-center align-items-center gap-1">
                              <button
                                type="button"
                                className="button btn btn-transparent px-1 text-green-500"
                                onClick={() => handleEditField(field, idx)}
                              >
                                <i className="bx bxs-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="button btn btn-transparent px-1 text-red-500"
                                onClick={() => onRemoveKeyValue(field.key)}
                              >
                                <i className="bx bx-trash" ></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};
